import { sql } from "drizzle-orm";
import type { WorkflowV2DbLike } from "./workflow-kernel-v2-store.js";
import { executeWorkflowV2Query } from "./workflow-kernel-v2-db.js";
import { workflowV2Jsonb } from "./workflow-kernel-v2-json.js";
import { GENCOW_APP_WAKE_CHANNEL, buildWakeSignalPayload } from "./wake-signal/wake-signal-types.js";

export type WorkflowV2TimerKind = "sleep" | "wait_timeout" | "retry_backoff" | "lifecycle_deadline";
const WORKFLOW_WAKE_SIGNAL_PAYLOAD = buildWakeSignalPayload("workflow");

export type WorkflowV2TimerRow = {
  id: string;
  run_id: string;
  kind: WorkflowV2TimerKind;
  fire_at: string | Date;
  status: string;
  dedupe_key: string | null;
};

export type WorkflowV2SignalRow = {
  id: string;
  run_id: string;
  event_name: string;
  payload_json: unknown;
  idempotency_key: string | null;
  received_at: string | Date;
  consumed_at: string | Date | null;
};

export type WorkflowV2OutboxRow = {
  id: string;
  run_id: string;
  kind: string;
  available_at: string | Date;
  status: string;
  attempt_count: number;
};

type WorkflowV2QueuedRunMissingOutboxRow = {
  id: string;
  workflow_name: string;
};

function rowsFromResult<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  if (result && typeof result === "object" && Array.isArray((result as { rows?: unknown[] }).rows)) {
    return (result as { rows: T[] }).rows;
  }
  return [];
}

export async function createWorkflowV2Timer(options: {
  db: WorkflowV2DbLike;
  id: string;
  runId: string;
  kind: WorkflowV2TimerKind;
  fireAt: string | Date;
  dedupeKey?: string | null;
}): Promise<WorkflowV2TimerRow> {
  const result = await executeWorkflowV2Query(
    options.db,
    sql`
        INSERT INTO _gencow_workflow_timers_v2 (
            id,
            run_id,
            kind,
            fire_at,
            dedupe_key
        )
        VALUES (
            ${options.id},
            ${options.runId},
            ${options.kind},
            ${options.fireAt}::timestamptz,
            ${options.dedupeKey ?? null}
        )
        ON CONFLICT (run_id, dedupe_key)
        DO UPDATE SET
            fire_at = EXCLUDED.fire_at,
            status = 'pending',
            updated_at = NOW()
        RETURNING *
    `,
  );
  const row = rowsFromResult<WorkflowV2TimerRow>(result)[0];
  if (!row) throw new Error(`failed to create workflow v2 timer "${options.id}"`);
  return row;
}

export async function getWorkflowV2TimerByDedupe(options: {
  db: WorkflowV2DbLike;
  runId: string;
  dedupeKey: string;
}): Promise<WorkflowV2TimerRow | null> {
  const result = await executeWorkflowV2Query(
    options.db,
    sql`
        SELECT *
        FROM _gencow_workflow_timers_v2
        WHERE run_id = ${options.runId}
          AND dedupe_key = ${options.dedupeKey}
        LIMIT 1
    `,
  );
  return rowsFromResult<WorkflowV2TimerRow>(result)[0] ?? null;
}

export async function fireDueWorkflowV2Timers(
  db: WorkflowV2DbLike,
  limit = 100,
): Promise<WorkflowV2TimerRow[]> {
  const result = await executeWorkflowV2Query(
    db,
    sql`
        WITH due AS (
            SELECT id
            FROM _gencow_workflow_timers_v2
            WHERE status = 'pending'
              AND fire_at <= NOW()
            ORDER BY fire_at ASC, created_at ASC
            FOR UPDATE SKIP LOCKED
            LIMIT ${limit}
        ),
        fired AS (
            UPDATE _gencow_workflow_timers_v2 timer
            SET
                status = 'fired',
                updated_at = NOW()
            FROM due
            WHERE timer.id = due.id
            RETURNING timer.*
        ),
        queued_runs AS (
            UPDATE _gencow_workflow_runs_v2 run
            SET
                status = 'queued',
                runnable_at = NOW(),
                lease_owner = NULL,
                lease_expires_at = NULL,
                heartbeat_at = NULL,
                updated_at = NOW()
            FROM fired
            WHERE run.id = fired.run_id
              AND fired.kind IN ('sleep', 'retry_backoff')
              AND run.status IN ('queued', 'running', 'sleeping', 'waiting')
              AND run.completed_at IS NULL
              AND run.cancel_requested_at IS NULL
            RETURNING run.id AS run_id, fired.id AS timer_id
        ),
        timed_out_runs AS (
            UPDATE _gencow_workflow_runs_v2 run
            SET
                status = 'failed',
                error_code = CASE
                    WHEN fired.kind = 'wait_timeout' THEN 'wait_timeout'
                    ELSE 'lifecycle_timeout'
                END,
                error_json = jsonb_build_object('timerId', fired.id, 'kind', fired.kind),
                lease_owner = NULL,
                lease_expires_at = NULL,
                heartbeat_at = NULL,
                completed_at = NOW(),
                updated_at = NOW()
            FROM fired
            WHERE run.id = fired.run_id
              AND fired.kind IN ('wait_timeout', 'lifecycle_deadline')
              AND run.status IN ('queued', 'running', 'sleeping', 'waiting')
              AND run.completed_at IS NULL
              AND NOT (
                fired.kind = 'lifecycle_deadline'
                AND run.status IN ('queued', 'running')
                AND run.current_step_key IS NULL
                AND run.active_started_at IS NULL
              )
            RETURNING run.id AS run_id, fired.id AS timer_id
        ),
        wake_outbox AS (
            INSERT INTO _gencow_workflow_outbox_v2 (
                id,
                run_id,
                kind,
                available_at,
                status
            )
            SELECT
                'timer:' || timer_id,
                run_id,
                'wake_timer',
                NOW(),
                'pending'
            FROM queued_runs
            ON CONFLICT (id) DO NOTHING
            RETURNING id
        )
        SELECT *
        FROM fired
        ORDER BY fire_at ASC
    `,
  );
  return rowsFromResult<WorkflowV2TimerRow>(result);
}

export async function recordWorkflowV2Signal(options: {
  db: WorkflowV2DbLike;
  id: string;
  runId: string;
  eventName: string;
  payload?: unknown;
  idempotencyKey?: string | null;
}): Promise<WorkflowV2SignalRow | null> {
  const result = await executeWorkflowV2Query(
    options.db,
    sql`
        WITH inserted AS (
            INSERT INTO _gencow_workflow_signals_v2 (
                id,
                run_id,
                event_name,
                payload_json,
                idempotency_key
            )
            VALUES (
                ${options.id},
                ${options.runId},
                ${options.eventName},
                ${workflowV2Jsonb(options.payload ?? {})},
                ${options.idempotencyKey ?? null}
            )
            ON CONFLICT (run_id, event_name, idempotency_key)
            DO NOTHING
            RETURNING *
        ),
        queued_run AS (
            UPDATE _gencow_workflow_runs_v2 run
            SET
                status = 'queued',
                runnable_at = NOW(),
                lease_owner = NULL,
                lease_expires_at = NULL,
                heartbeat_at = NULL,
                updated_at = NOW()
            FROM inserted
            WHERE run.id = inserted.run_id
              AND run.status = 'waiting'
              AND run.completed_at IS NULL
              AND run.cancel_requested_at IS NULL
            RETURNING run.id
        ),
        wake_outbox AS (
            INSERT INTO _gencow_workflow_outbox_v2 (
                id,
                run_id,
                kind,
                available_at,
                status
            )
            SELECT
                'signal:' || inserted.id,
                inserted.run_id,
                'wake_signal',
                NOW(),
                'pending'
            FROM inserted
            JOIN queued_run ON queued_run.id = inserted.run_id
            ON CONFLICT (id) DO NOTHING
            RETURNING id
        ),
        wake_notified AS (
            SELECT pg_notify(${GENCOW_APP_WAKE_CHANNEL}, ${WORKFLOW_WAKE_SIGNAL_PAYLOAD})
            FROM wake_outbox
        )
        SELECT inserted.*
        FROM inserted
        LEFT JOIN wake_notified ON TRUE
    `,
  );
  return rowsFromResult<WorkflowV2SignalRow>(result)[0] ?? null;
}

export async function claimWorkflowV2Signal(options: {
  db: WorkflowV2DbLike;
  runId: string;
  eventName: string;
}): Promise<WorkflowV2SignalRow | null> {
  const result = await executeWorkflowV2Query(
    options.db,
    sql`
        WITH candidate AS (
            SELECT id
            FROM _gencow_workflow_signals_v2
            WHERE run_id = ${options.runId}
              AND event_name = ${options.eventName}
              AND consumed_at IS NULL
            ORDER BY received_at ASC, id ASC
            FOR UPDATE SKIP LOCKED
            LIMIT 1
        )
        UPDATE _gencow_workflow_signals_v2 signal
        SET consumed_at = NOW()
        WHERE signal.id = (SELECT id FROM candidate)
        RETURNING *
    `,
  );
  return rowsFromResult<WorkflowV2SignalRow>(result)[0] ?? null;
}

export async function enqueueWorkflowV2Outbox(options: {
  db: WorkflowV2DbLike;
  id: string;
  runId: string;
  kind: string;
  availableAt?: string | Date;
}): Promise<WorkflowV2OutboxRow> {
  const result = await executeWorkflowV2Query(
    options.db,
    sql`
        INSERT INTO _gencow_workflow_outbox_v2 (
            id,
            run_id,
            kind,
            available_at
        )
        VALUES (
            ${options.id},
            ${options.runId},
            ${options.kind},
            ${options.availableAt ?? sql`NOW()`}
        )
        ON CONFLICT (id)
        DO UPDATE SET
            available_at = EXCLUDED.available_at,
            status = 'pending',
            updated_at = NOW()
        RETURNING *
    `,
  );
  const row = rowsFromResult<WorkflowV2OutboxRow>(result)[0];
  if (!row) throw new Error(`failed to enqueue workflow v2 outbox "${options.id}"`);
  return row;
}

export async function enqueueMissingQueuedWorkflowV2Outbox(options: {
  db: WorkflowV2DbLike;
  workflowNames: Iterable<string>;
  limit?: number;
}): Promise<WorkflowV2OutboxRow[]> {
  const allowedNames = new Set(options.workflowNames);
  if (allowedNames.size === 0) return [];

  const candidateResult = await executeWorkflowV2Query(
    options.db,
    sql`
        SELECT run.id, run.workflow_name
        FROM _gencow_workflow_runs_v2 run
        WHERE run.status = 'queued'
          AND run.completed_at IS NULL
          AND run.cancel_requested_at IS NULL
          AND run.runnable_at <= NOW()
          AND run.updated_at <= NOW() - INTERVAL '10 seconds'
          AND NOT EXISTS (
            SELECT 1
            FROM _gencow_workflow_outbox_v2 outbox
            WHERE outbox.run_id = run.id
              AND outbox.status IN ('pending', 'sending')
          )
          AND NOT EXISTS (
            SELECT 1
            FROM _gencow_workflow_outbox_v2 outbox
            WHERE outbox.run_id = run.id
              AND outbox.status = 'sent'
              AND outbox.updated_at >= NOW() - INTERVAL '60 seconds'
          )
        ORDER BY run.runnable_at ASC, run.created_at ASC
        LIMIT ${options.limit ?? 100}
    `,
  );
  const candidates = rowsFromResult<WorkflowV2QueuedRunMissingOutboxRow>(candidateResult).filter((run) =>
    allowedNames.has(run.workflow_name),
  );
  const repaired: WorkflowV2OutboxRow[] = [];

  for (const run of candidates) {
    const outbox = await enqueueWorkflowV2Outbox({
      db: options.db,
      id: `repair:${run.id}`,
      runId: run.id,
      kind: "wake_repair",
    });
    await executeWorkflowV2Query(
      options.db,
      sql`
        UPDATE _gencow_workflow_runs_v2
        SET updated_at = NOW()
        WHERE id = ${run.id}
          AND status = 'queued'
          AND completed_at IS NULL
    `,
    );
    repaired.push(outbox);
  }

  return repaired;
}

export async function claimDueWorkflowV2Outbox(
  db: WorkflowV2DbLike,
  limit = 100,
  workflowNames?: Iterable<string>,
): Promise<WorkflowV2OutboxRow[]> {
  const allowedNames = workflowNames ? [...new Set(workflowNames)] : [];
  const workflowFilter =
    allowedNames.length === 0
      ? sql``
      : sql`
              AND EXISTS (
                  SELECT 1
                  FROM _gencow_workflow_runs_v2 run
                  WHERE run.id = outbox.run_id
                    AND run.workflow_name IN (${sql.join(
                      allowedNames.map((name) => sql`${name}`),
                      sql`, `,
                    )})
              )
          `;
  const result = await executeWorkflowV2Query(
    db,
    sql`
        WITH candidate AS (
            SELECT outbox.id
            FROM _gencow_workflow_outbox_v2 outbox
            WHERE outbox.status = 'pending'
              AND outbox.available_at <= NOW()
              ${workflowFilter}
            ORDER BY outbox.available_at ASC, outbox.created_at ASC
            FOR UPDATE SKIP LOCKED
            LIMIT ${limit}
        )
        UPDATE _gencow_workflow_outbox_v2 outbox
        SET
            status = 'sending',
            attempt_count = attempt_count + 1,
            updated_at = NOW()
        FROM candidate
        WHERE outbox.id = candidate.id
        RETURNING outbox.*
    `,
  );
  return rowsFromResult<WorkflowV2OutboxRow>(result);
}

export async function markWorkflowV2OutboxSent(db: WorkflowV2DbLike, outboxId: string): Promise<boolean> {
  const result = await executeWorkflowV2Query(
    db,
    sql`
        UPDATE _gencow_workflow_outbox_v2
        SET
            status = 'sent',
            updated_at = NOW()
        WHERE id = ${outboxId}
          AND status = 'sending'
        RETURNING id
    `,
  );
  return rowsFromResult<{ id: string }>(result).length === 1;
}
